import { Config } from "../config/Config";
import { LanguageConstants } from "../constants/LanguageConstants";

/** Shared checkout helpers for quotation approve / payment flow. */

/** Pathname without CRA `homepage` / Router basename (e.g. `/rc-part-front-dev`). */
export function getAppRouterPath(pathname: string): string {
  const base = String(Config.BASE_URL || "").replace(/\/$/, "");
  const p = pathname || "/";
  if (!base) {
    return p.startsWith("/") ? p : `/${p}`;
  }
  if (p === base || p.startsWith(`${base}/`)) {
    const rest = p.slice(base.length) || "/";
    return rest.startsWith("/") ? rest : `/${rest}`;
  }
  return p.startsWith("/") ? p : `/${p}`;
}

/**
 * Public quotation id (50000+…) for checkout — never the order uuid (40000+…).
 * Prefer nested `quotation_detail` over order root `quotation_id`.
 */
export function getOrderQuotationPublicId(order?: {
  uuid?: unknown;
  quotation_id?: unknown;
  quotation_detail?: { quotation_id?: unknown } | null;
} | null): string | null {
  const quoteId = order?.quotation_detail?.quotation_id ?? order?.quotation_id;
  if (quoteId == null || String(quoteId).trim() === "") return null;
  const q = String(quoteId).trim();
  const orderUuid = order?.uuid != null ? String(order.uuid).trim() : "";
  if (orderUuid && q === orderUuid) return null;
  return q;
}

export function getQuotationTaxAmount(data: {
  tax_amount?: unknown;
  total_amount?: unknown;
  total_shipping_price?: unknown;
  total_product_price?: unknown;
  other_charges?: unknown;
} | null | undefined): string {
  if (data?.tax_amount && data?.total_amount) {
    const total =
      Number(data?.total_shipping_price) +
      Number(data?.total_product_price) +
      Number(data?.other_charges || 0);
    const taxValue = (total * Number(data?.tax_amount)) / 100;
    return taxValue.toFixed(2);
  }
  return "0.00";
}

/** Address line 1 + line 2 only (no separate city/state/zip/country). */
function joinAddressLine1And2(line1Raw: unknown, line2Raw: unknown): string {
  const line1 = line1Raw != null ? String(line1Raw).trim() : "";
  const line2 = line2Raw != null ? String(line2Raw).trim() : "";
  if (!line1 && !line2) return "—";
  if (!line2) return line1;
  if (!line1) return line2;
  if (line2.toLowerCase() === line1.toLowerCase()) return line1;
  return `${line1}, ${line2}`.replace(/\s+/g, " ").trim();
}

export function formatCheckoutAddressLine(data: {
  address_detail?: {
    street_line?: string;
    appartment?: string;
  };
} | null | undefined): string {
  const addr = data?.address_detail;
  if (!addr) return "—";
  return joinAddressLine1And2(addr.street_line, addr.appartment);
}

/** Merge quotation + order address (API often omits instructions/residence on nested quotation address). */
export function mergeOrderShippingAddress(order?: {
  address_detail?: Record<string, unknown> | null;
  quotation_detail?: {
    address_detail?: Record<string, unknown> | null;
  } | null;
} | null): Record<string, unknown> | null {
  const q = order?.quotation_detail?.address_detail;
  const od = order?.address_detail;
  if (!q && !od) return null;
  return {
    ...(od && typeof od === "object" ? od : {}),
    ...(q && typeof q === "object" ? q : {}),
    instructions:
      (q as { instructions?: unknown })?.instructions ??
      (od as { instructions?: unknown })?.instructions,
    loaction_type:
      (q as { loaction_type?: unknown })?.loaction_type ??
      (od as { loaction_type?: unknown })?.loaction_type,
    country:
      (q as { country?: unknown })?.country ??
      (od as { country?: unknown })?.country,
  };
}

/** Delivery address on order detail / order checkout (quotation address, else order snapshot). */
export function buildOrderDeliveryAddressLines(order?: {
  street_line?: unknown;
  appartment?: unknown;
  zip_code?: unknown;
  city?: unknown;
  state?: unknown;
  loaction_type?: unknown;
  address_detail?: Record<string, unknown> | null;
  quotation_detail?: {
    address_detail?: Record<string, unknown> | null;
  } | null;
} | null): string {
  const a = mergeOrderShippingAddress(order) ?? order;
  if (!a || typeof a !== "object") return "—";
  return joinAddressLine1And2(a.street_line, a.appartment);
}

export function getOrderShippingInstructions(order?: {
  address_detail?: { instructions?: unknown } | null;
  quotation_detail?: {
    address_detail?: { instructions?: unknown } | null;
  } | null;
} | null): string {
  const merged = mergeOrderShippingAddress(order);
  const raw = merged?.instructions;
  if (raw != null && String(raw).trim() !== "") {
    return String(raw).trim();
  }
  return "—";
}

export function getOrderResidenceLabel(order?: {
  loaction_type?: unknown;
  address_detail?: { loaction_type?: unknown } | null;
  quotation_detail?: {
    address_detail?: { loaction_type?: unknown } | null;
  } | null;
} | null): string {
  const merged = mergeOrderShippingAddress(order);
  const t = merged?.loaction_type ?? order?.loaction_type;
  return getCheckoutResidenceLabel(t);
}

export function getCheckoutResidenceLabel(loactionType: unknown): string {
  const t = loactionType != null ? String(loactionType).trim() : "";
  if (t === "1") return LanguageConstants?.RESIDENTIAL ?? "Residential";
  if (t === "2") return LanguageConstants?.LABORAL ?? "Business";
  if (t === "3") return LanguageConstants?.INDUSTRIAL ?? "Industrial";
  return "—";
}

export function getCheckoutItemCount(data: { quotes_detail?: unknown[] } | null | undefined): number {
  const rows = data?.quotes_detail;
  if (!Array.isArray(rows)) return 0;
  return rows.filter((row: any) => String(row?.is_deleted ?? "0") !== "1").length;
}
